import React, { useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import PopoverPanel from '../popover-panel'
import { colors } from '../tokens'

const useStyles = makeStyles({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
    whiteSpace: 'nowrap',
  },
  chevron: {
    width: 14,
    height: 14,
    color: colors.muted,
  },
  list: {
    padding: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 8px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    fontSize: '13px',
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
  optionLabel: {
    flex: 1,
  },
  check: {
    width: 14,
    height: 14,
    color: colors.primary,
  },
})

/**
 * Bordered button showing the selected option; opens a list of options.
 *
 * @param {{ value: string, label: string }[]} options
 */
const SelectButton = ({ options, value, onChange, width = 220 }) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const selected = options.find(option => option.value === value) || options[0]

  return (
    <PopoverPanel
      open={open}
      onClose={close}
      width={width}
      align="right"
      trigger={
        <button
          type="button"
          className={classes.trigger}
          onClick={() => setOpen(current => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selected.label}
          <Icon icon="tabler:chevron-down" className={classes.chevron} />
        </button>
      }
    >
      <div className={classes.list} role="listbox">
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={option.value === value}
            className={classes.option}
            onClick={() => {
              onChange(option.value)
              close()
            }}
          >
            <span className={classes.optionLabel}>{option.label}</span>
            {option.value === value && (
              <Icon icon="tabler:check" className={classes.check} />
            )}
          </button>
        ))}
      </div>
    </PopoverPanel>
  )
}

export default SelectButton
